/**
 * `/api/v1/*` payloads — the decommissioned License Leasing API's own surface.
 *
 * `swarm-license-staking-api` (FastAPI) is being folded into this API. Its
 * browser traffic already arrives through `/api/license-leases/*`, but anything
 * that called the service DIRECTLY — an admin dashboard, a script, a future
 * integration — holds `/api/v1/...` URLs and an `X-API-Key`. This group
 * reproduces those paths, that credential and these envelopes byte for byte, so
 * the cutover is a hostname swap and nothing else.
 *
 * The record shapes (`leaseRecordSchema`, `licenseRecordSchema`, …) are shared
 * with `license-leases.ts` — the two groups return the SAME rows, and there is
 * exactly one declaration of what a lease looks like on the wire.
 *
 * TWO ERROR ENVELOPES, on purpose. FastAPI answered a raw `HTTPException` with
 * `{"detail": "..."}` and the app's own typed exceptions with
 * `{"error": {"code", "message", "details"}}`. Which one a caller got depended on
 * which validation tripped first, and that inconsistency is part of the
 * contract: a client branching on `body.detail` must keep working.
 */
import { z } from "zod";

import { suiAddressSchema } from "../primitives";
import { leaseRecordSchema } from "./license-leases";

/** Response for `GET /api/v1/health`. */
export const leasingHealthResponseSchema = z.object({
  status: z.enum(["healthy", "unhealthy"]),
  version: z.string(),
  database: z.boolean(),
  /** `datetime.utcnow().isoformat()` — note: no trailing `Z`, as upstream. */
  timestamp: z.string(),
});

/** Resolved health response. */
export type LeasingHealthResponse = z.infer<typeof leasingHealthResponseSchema>;

/**
 * `{"error": {...}}` — the envelope FastAPI's registered exception handlers
 * produced (`ValidationError`, `NotFoundError`, `ConflictError`, …).
 */
export const leasingErrorResponseSchema = z.object({
  error: z.object({
    /** `VALIDATION_ERROR`, `NOT_FOUND`, `CONFLICT_ERROR`, `RATE_LIMIT_EXCEEDED`, … */
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});

/** Resolved typed-exception envelope. */
export type LeasingErrorResponse = z.infer<typeof leasingErrorResponseSchema>;

/**
 * `{"detail": "..."}` — FastAPI's built-in envelope for a bare `HTTPException`.
 * Answered by the auth, wallet-format and rate-limit checks.
 */
export const leasingDetailResponseSchema = z.object({
  detail: z.string(),
});

/** Resolved bare-HTTPException envelope. */
export type LeasingDetailResponse = z.infer<typeof leasingDetailResponseSchema>;

/**
 * Body for `POST /api/v1/leases`.
 *
 * `quantity`, not a list of ids: the caller says how many licenses to lock and
 * the server picks that many of the wallet's unlocked ones. The upstream chose
 * them with `random.sample`; this port takes the oldest first — see the note on
 * `createLease` in `apps/api/src/lib/leases.ts`.
 *
 * The address is lower-cased, matching the upstream's `field_validator`.
 */
export const createLeaseRequestSchema = z.object({
  owner: suiAddressSchema.transform((value) => value.toLowerCase()),
  quantity: z.number().int().positive(),
  duration_months: z.union([z.literal(6), z.literal(12), z.literal(18)]),
});

/** Resolved lease-creation body. */
export type CreateLeaseRequest = z.infer<typeof createLeaseRequestSchema>;

/**
 * Owner path parameter for the `/api/v1` reads.
 *
 * A bare string, NOT `suiAddressSchema`. The format check happens in the handler
 * via `validateWalletAddress`, because rejecting it in the schema would answer
 * Elysia's `{"error": "Invalid request (params.owner): …"}` where the upstream
 * answered `{"detail": "Invalid wallet address format"}` — and a caller
 * branching on `detail` would stop seeing the reason.
 */
export const leasingOwnerParamsSchema = z.object({
  owner: z.string(),
});

/** Resolved `/api/v1` owner path parameter. */
export type LeasingOwnerParams = z.infer<typeof leasingOwnerParamsSchema>;

/** Query for `GET /api/v1/leases/{owner}`. Optional limit/offset pagination. */
export const leasingLeasesQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(1000).optional(),
  offset: z.coerce.number().int().min(0).default(0),
});

/** Resolved lease-list query. */
export type LeasingLeasesQuery = z.infer<typeof leasingLeasesQuerySchema>;

/** Response for `POST /api/v1/leases`: the lease that was just created. */
export const leasingCreateLeaseResponseSchema = leaseRecordSchema;

/** Resolved `/api/v1/leases` creation response. */
export type LeasingCreateLeaseResponse = z.infer<typeof leasingCreateLeaseResponseSchema>;

/**
 * Response for `GET /api/v1/admin/stats`.
 *
 * Numbers, not formatted strings. The upstream had two different versions of
 * this payload — `routers/admin.py` returned raw numbers to HTTP callers while
 * `LicenseStakingManager.get_system_stats` returned `"{:,.2f}"` strings for the
 * Telegram message. This is the HTTP one; the notifier's formatting now lives in
 * `apps/jobs/src/notify/`.
 */
export const leasingAdminStatsResponseSchema = z.object({
  system_overview: z.object({
    total_active_leases: z.number().int(),
    total_expired_leases: z.number().int(),
    total_licenses_staked: z.number().int(),
    total_active_weight: z.number(),
    daily_leasing_pool: z.number(),
  }),
  rewards_overview: z.object({
    total_accumulated_leasing_rewards: z.number(),
    /** Same figure as above. Duplicated upstream; kept so callers do not break. */
    total_rewards_distributed: z.number(),
  }),
  lease_analytics: z.object({
    average_lease_duration_months: z.number(),
    average_licenses_per_lease: z.number(),
    duration_distribution: z.object({
      "6_months": z.number().int(),
      "12_months": z.number().int(),
      "18_months": z.number().int(),
    }),
  }),
  system_health: z.object({
    database_connected: z.boolean(),
    last_updated: z.string(),
    api_version: z.string(),
  }),
});

/** Resolved admin-stats response. */
export type LeasingAdminStatsResponse = z.infer<typeof leasingAdminStatsResponseSchema>;
